import React, { useState } from 'react';
import { useAuthContext } from '../../../contexts';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Button from '@material-ui/core/Button';
import { createAmazonUrl } from '../../Amazon/util';
import { createAmazonSellerAccount } from '../../API/amazon';
import { useSnackbarContext } from 'frontend/src/contexts/Snackbar';
import { ReactComponent as AmazonIcon } from '../../../images/amazon.svg';
import { ReactComponent as CheckmarkIcon } from '../../../images/checkmark.svg';
import LoadingBackdrop from '../../LoadingBackdrop';
import AccountFooterText from './AccountFooterText';
import { useEventTrackerContext } from 'frontend/src/contexts/EventTracker';
import { useMobile } from 'frontend/src/hooks/useMobile';
import BackButton from '../BuildingBlocks/Buttons/BackButton';
import { useQuery } from 'frontend/src/hooks/useQuery';

const useStyles = makeStyles((theme: any) => ({
  topContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginTop: 0,
    margin: theme.spacing(2),
  },
  titleContent: {
    fontWeight: 'bold',
    fontSize: 24,
    lineHeight: '30px',
    textAlign: 'center',
    marginBottom: theme.spacing(2),
  },
  subtitleContent: {
    fontSize: 18,
    lineHeight: '24px',
    textAlign: 'center',
    marginBottom: theme.spacing(2),
  },
  iconsContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: 194,
    height: 96,
    border: '2px solid rgba(0, 0, 0, 0.08)',
    boxSizing: 'border-box',
    borderRadius: 12,
  },
  connectedIconsContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: 194,
    height: 128,
    border: '2px solid #00DDA9',
    boxSizing: 'border-box',
    borderRadius: 12,
  },
  connectedIcon: {
    marginTop: theme.spacing(1),
  },
  button: {
    minWidth: 130,
    fontSize: theme.fontSizes.onboarding,
    marginRight: theme.spacing(0.5),
    marginLeft: theme.spacing(0.5),
  },
  buttonContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    marginTop: theme.spacing(6),
  },
}));

const Subtitle =
  'Our team of data scientists relies on Amazon data to forecast your sales in real-time.';

export default function ConnectAmazonStore({
  business,
  goForward,
  amzAccounts,
  lines,
}) {
  const classes = useStyles();
  const snackbar = useSnackbarContext();
  const mobile = useMobile();
  const { firstName } = useAuthContext();
  const { trackEvent } = useEventTrackerContext();
  const [isConnecting, setIsConnecting] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const query = useQuery();
  const demoSellerId = query.get('demoConnect');
  const hasAmzAccount =
    demoSellerId ||
    amzAccounts.filter((account) => account.sellerId).length > 0;

  function handleSubmit() {
    if (!hasAmzAccount) {
      try {
        setIsConnecting(true);
        setIsProcessing(true);
        handleConnect(mobile ? window : null);
      } catch (error) {
        snackbar.show(
          'Oops, we encountered an issue while connecting to Amazon Seller Central. Please try again.',
          'error',
        );
        setIsConnecting(false);
        setIsProcessing(false);
      }
    } else {
      demoConnect();
      trackEvent({
        event: 'click_continue_amazon_account',
      });
      goForward();
    }
  }

  function demoConnect() {
    if (demoSellerId) {
      trackEvent({
        event: 'click_connect_amazon_account',
      });
    }
  }

  function checkWindowRefBlocked(windowRef): boolean {
    if (windowRef === null) {
      snackbar.show(
        'Please enable pop-ups for this site. Reload and try again.',
        'error',
      );
      setIsConnecting(false);
      setIsProcessing(false);
      return true;
    } else {
      return false;
    }
  }

  async function handleConnect(windowRef: any = null) {
    const response = await createAmazonSellerAccount(
      business.name || 'Amazon Account',
    );
    const { id } = await response.json();
    trackEvent({
      event: 'click_connect_amazon_account',
    });

    setIsProcessing(false);
    const amzUrl = createAmazonUrl(id, lines);
    if (windowRef) {
      windowRef.location = amzUrl;
    } else {
      const newWindowRef = window.open(amzUrl);
      if (checkWindowRefBlocked(newWindowRef)) {
        return;
      }
    }
  }

  const getHeader = () => {
    if (hasAmzAccount) {
      return 'Your store is successfully connected.';
    } else if (isConnecting) {
      return 'Connecting account...';
    } else {
      return `Hey${
        firstName ? ` ${firstName}` : ''
      }, Let's connect your sales platform.`;
    }
  };

  return (
    <>
      <div className={classes.topContent}>
        <LoadingBackdrop open={isProcessing} />
        <div className={classes.titleContent}>{getHeader()}</div>
        {!(isConnecting || hasAmzAccount) && (
          <div className={classes.subtitleContent}>{Subtitle}</div>
        )}
        <div
          className={
            hasAmzAccount ? classes.connectedIconsContent : classes.iconsContent
          }
        >
          <AmazonIcon />
          {hasAmzAccount && <CheckmarkIcon className={classes.connectedIcon} />}
        </div>
      </div>
      <div className={classes.buttonContainer}>
        <BackButton />
        <Button
          onClick={handleSubmit}
          className={classes.button}
          color="primary"
          type="submit"
          variant="contained"
          disabled={isConnecting}
          data-automation-id="submit"
        >
          {hasAmzAccount ? 'Continue' : 'Connect'}
        </Button>
      </div>
      <AccountFooterText />
    </>
  );
}
